import React, { useState } from 'react';
import { VenueBooking } from '../../types/venue';
import { theme } from '../../theme';
import BookingStatusBadge from './BookingStatusBadge';
import {
  formatPrice,
  formatDate,
  formatTime,
} from '../../utils/venueHelpers';
import {
  MapPin,
  Calendar,
  Clock,
  DollarSign,
  CheckCircle,
  Eye,
  Mail,
  Phone,
  User,
} from 'lucide-react';

interface OwnerBookingCardProps {
  booking: VenueBooking;
  onConfirm?: (bookingId: string) => void;
  onComplete?: (bookingId: string) => void;
  highlighted?: boolean;
}

const OwnerBookingCard: React.FC<OwnerBookingCardProps> = ({
  booking,
  onConfirm,
  onComplete,
  highlighted = false,
}) => {
  const [confirmingAction, setConfirmingAction] = useState<'confirm' | 'complete' | null>(null);

  const canConfirm = booking.status === 'PENDING';
  const canComplete = booking.status === 'CONFIRMED';

  const handleConfirm = () => {
    setConfirmingAction('confirm');
  };

  const handleConfirmYes = () => {
    if (onConfirm) {
      onConfirm(booking.id);
    }
    setConfirmingAction(null);
  };

  const handleComplete = () => {
    setConfirmingAction('complete');
  };

  const handleCompleteYes = () => {
    if (onComplete) {
      onComplete(booking.id);
    }
    setConfirmingAction(null);
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '20px',
    marginBottom: '16px',
    border: highlighted
      ? `3px solid ${theme.colors.warning}`
      : `2px solid ${theme.colors.border}`,
    transition: 'border-color 0.2s',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  };

  const venueNameStyle: React.CSSProperties = {
    fontSize: '1.2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
    fontFamily: theme.fonts.heading,
  };

  const customerSectionStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.input,
    padding: '12px',
    marginBottom: '16px',
  };

  const customerTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.textSecondary,
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const customerInfoStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.text,
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const infoGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '16px',
  };

  const infoItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const priceContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.input,
    marginBottom: '16px',
  };

  const priceLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const priceValueStyle: React.CSSProperties = {
    fontSize: '1.3rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.primary,
  };

  const actionsContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  };

  const buttonBaseStyle: React.CSSProperties = {
    padding: '10px 16px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: theme.colors.primary,
    color: '#000',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: 'transparent',
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
  };

  const confirmationCodeStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginTop: '8px',
  };

  const confirmModalStyle: React.CSSProperties = {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.input,
    border: `2px solid ${theme.colors.primary}`,
  };

  const confirmTextStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.text,
    marginBottom: '12px',
  };

  const confirmActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
  };

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ flex: 1 }}>
          <h3 style={venueNameStyle}>{booking.venue?.name || 'Cancha'}</h3>
          <BookingStatusBadge status={booking.status} />
        </div>
      </div>

      {/* Customer Info */}
      <div style={customerSectionStyle}>
        <div style={customerTitleStyle}>
          <User size={14} />
          Cliente
        </div>
        <div style={customerInfoStyle}>
          <span style={{ fontWeight: theme.fonts.weightBold }}>
            {booking.booker?.name || 'Usuario'}
          </span>
        </div>
        {booking.booker?.email && (
          <div style={customerInfoStyle}>
            <Mail size={14} />
            <span>{booking.booker.email}</span>
          </div>
        )}
        {booking.booker?.phone && (
          <div style={customerInfoStyle}>
            <Phone size={14} />
            <span>{booking.booker.phone}</span>
          </div>
        )}
      </div>

      {/* Info Grid */}
      <div style={infoGridStyle}>
        <div style={infoItemStyle}>
          <Calendar size={16} />
          <span>{formatDate(booking.bookingDate)}</span>
        </div>
        <div style={infoItemStyle}>
          <Clock size={16} />
          <span>
            {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
          </span>
        </div>
        <div style={infoItemStyle}>
          <MapPin size={16} />
          <span>{booking.venue?.city || 'Ciudad'}</span>
        </div>
        <div style={infoItemStyle}>
          <Clock size={16} />
          <span>{booking.durationHours} hora(s)</span>
        </div>
      </div>

      {/* Price */}
      <div style={priceContainerStyle}>
        <div style={priceLabelStyle}>
          <DollarSign size={14} style={{ display: 'inline', marginRight: '4px' }} />
          Total
        </div>
        <div style={priceValueStyle}>{formatPrice(booking.totalAmount)}</div>
      </div>

      {/* Confirmation Code */}
      <div style={confirmationCodeStyle}>
        Código de confirmación: <strong>{booking.confirmationCode}</strong>
      </div>

      {/* Actions */}
      {!confirmingAction && (
        <div style={actionsContainerStyle}>
          {/* Confirm Button */}
          {canConfirm && onConfirm && (
            <button
              style={primaryButtonStyle}
              onClick={handleConfirm}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#00D96F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
              }}
            >
              <CheckCircle size={16} />
              Confirmar reserva
            </button>
          )}

          {/* Complete Button */}
          {canComplete && onComplete && (
            <button
              style={primaryButtonStyle}
              onClick={handleComplete}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#00D96F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
              }}
            >
              <CheckCircle size={16} />
              Marcar como completada
            </button>
          )}

          {/* View Detail Button - Always available */}
          <button
            style={secondaryButtonStyle}
            onClick={() => {
              // Navigate to booking detail or show more info
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.background;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Eye size={16} />
            Ver detalle
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmingAction === 'confirm' && (
        <div style={confirmModalStyle}>
          <div style={confirmTextStyle}>
            ¿Confirmar esta reserva? El cliente recibirá una notificación.
          </div>
          <div style={confirmActionsStyle}>
            <button
              style={primaryButtonStyle}
              onClick={handleConfirmYes}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#00D96F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
              }}
            >
              Sí, confirmar
            </button>
            <button
              style={secondaryButtonStyle}
              onClick={() => setConfirmingAction(null)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.background;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {confirmingAction === 'complete' && (
        <div style={confirmModalStyle}>
          <div style={confirmTextStyle}>
            ¿Marcar como completada? Esto indica que el partido ya se jugó.
          </div>
          <div style={confirmActionsStyle}>
            <button
              style={primaryButtonStyle}
              onClick={handleCompleteYes}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#00D96F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
              }}
            >
              Sí, completar
            </button>
            <button
              style={secondaryButtonStyle}
              onClick={() => setConfirmingAction(null)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.background;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerBookingCard;
